import { useEffect, useRef } from "react";

import type { ToastManager } from "../Toast";
import { annotationStudioText } from "./messages";
import type {
  AnnotationSetValidationIssue,
  AnnotationSetValidationState,
  AnnotationStudioLocale,
} from "./types";

function relatedFrameIds(issue: AnnotationSetValidationIssue): readonly string[] {
  const frameIds = [issue.frameId, ...(issue.affectedFrameIds ?? [])].filter(
    (frameId): frameId is string => Boolean(frameId),
  );
  return [...new Set(frameIds)].sort();
}

function validationGuidanceSignature(
  locale: AnnotationStudioLocale,
  issues: readonly AnnotationSetValidationIssue[],
): string {
  return issues
    .map((issue) =>
      [
        issue.id,
        issue.code,
        issue.severity,
        issue.targetId ?? "",
        relatedFrameIds(issue).join(","),
      ].join("|"),
    )
    .sort()
    .concat(locale)
    .join(";");
}

/**
 * Empty-set validation is actionable authoring guidance rather than a conflict announcement.
 * Semantic and revision conflicts deliberately stay out of Toast: persistent indicators own the
 * in-progress state and the Studio exit preflight owns the blocking review Modal.
 */
export function useAnnotationValidationGuidanceToast({
  manager,
  validation,
  locale,
  onReviewValidation,
}: {
  manager: ToastManager;
  validation: AnnotationSetValidationState | null;
  locale: AnnotationStudioLocale;
  onReviewValidation: () => void;
}): void {
  const validationSignatureRef = useRef<string | null>(null);
  const validationToastIdRef = useRef<string | null>(null);
  const onReviewValidationRef = useRef(onReviewValidation);
  onReviewValidationRef.current = onReviewValidation;

  useEffect(
    () => () => {
      if (validationToastIdRef.current) manager.dismiss(validationToastIdRef.current);
      validationSignatureRef.current = null;
      validationToastIdRef.current = null;
    },
    [manager],
  );

  useEffect(() => {
    const issues = validation?.issues ?? [];
    const needsEmptySetGuidance =
      validation?.status === "blocked" &&
      issues.length > 0 &&
      issues.every((issue) => issue.code === "empty-annotation-set");
    if (!needsEmptySetGuidance) {
      if (validationToastIdRef.current) manager.dismiss(validationToastIdRef.current);
      validationToastIdRef.current = null;
      validationSignatureRef.current = null;
      return;
    }

    const signature = validationGuidanceSignature(locale, issues);
    if (signature === validationSignatureRef.current) return;

    if (validationToastIdRef.current) manager.dismiss(validationToastIdRef.current);
    validationSignatureRef.current = signature;
    const showToast = issues.some((issue) => issue.severity === "error")
      ? manager.error
      : manager.warning;
    validationToastIdRef.current = showToast(
      annotationStudioText("annotationSetNeedsAnnotation", locale),
      {
        action: {
          label: annotationStudioText("reviewAnnotationConflicts", locale),
          onPress: () => onReviewValidationRef.current(),
        },
      },
    );
  }, [locale, manager, validation]);
}
